import type {Thing, WithContext} from 'schema-dts';
import {callOnce, useHead, useState} from '#imports';

/**
 * SSR friendly composable to add structured data to the head.
 * This composable is called only once.
 *
 * @param structuredDataFactoryFunction A function that returns an array of structured data objects (aka Thing), this function is called only once.
 */
export default function useStructuredData() {
    const structuredData = useState<Thing[]>('malt-structured-data-state', () => []);

    // Reset the structured data array during navigation
    callOnce('structured-data', () => (structuredData.value = []), {mode: 'navigation'});

    useHead({
        script: structuredData.value ? () => toLdJson(structuredData.value!) : [],
    });

    function toLdJson<T extends Thing>(structuredDataObject: T[]) {
        return structuredDataObject.map((object: T) => ({
            type: 'application/ld+json',
            innerHTML: JSON.stringify({
                '@context': 'https://schema.org',
                ...(object as object),
            } as WithContext<T>),
        }));
    }

    /**
     * Define a structured data object (once). This function is SSR friendly, and will be called during navigation.
     * @param key The key of the structured data object.
     * @param structuredDataObject The structured data object.
     */
    function defineStructuredData(key: string, structuredDataObject: Thing) {
        callOnce(`structured-data--${key}`, () => structuredData.value.push(structuredDataObject), {mode: 'navigation'});
    }

    return {
        // functions a-z
        defineStructuredData,
    };
}
